import { useActions } from 'hooks';
import { Drawer, Icon, ItemTitle, styled, useTheme } from 'hyarchis-global';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  closeDrawer as closeDrawerAction,
  updateDrawerWidth as updateDrawerWidthAction,
} from 'store/drawers/actions';
import { DrawerType, OpenDrawerParams } from 'types/drawers';
import SettingsEditDrawer from './SettingsEditDrawer';
import handleDrawerClose from './drawerCloseHandler';

const StyledDrawer = styled(Drawer)<{ selectedItemsExist: boolean }>`
  height: 100%;
`;

const defaultDrawerMinWidth = 300;

interface OwnProps {
  activeDrawerParams?: OpenDrawerParams;
  drawerWidth?: number;
  isResizable?: boolean;
  selectedItemsExist: boolean;
}

const DrawerPicker = ({
  activeDrawerParams,
  drawerWidth,
  isResizable,
  selectedItemsExist,
}: OwnProps) => {
  const theme = useTheme();

  const { closeDrawer, updateDrawerWidth } = useActions({
    closeDrawer: closeDrawerAction,
    updateDrawerWidth: updateDrawerWidthAction,
  });

  const [overriddenDrawerTitle, setOverriddenDrawerTitle] = useState<JSX.Element>();
  const [formIsEdited, setFormIsEdited] = useState(false);

  const handleResetState = useCallback(() => {
    setOverriddenDrawerTitle(undefined);
  }, []);

  useEffect(() => {
    handleResetState();
  }, [activeDrawerParams, handleResetState]);

  useEffect(() => {
    if (!activeDrawerParams) {
      setFormIsEdited(false);
    }
  }, [activeDrawerParams]);

  const drawerIcon = useMemo(() => {
    if (!activeDrawerParams) {
      return undefined;
    }

    switch (activeDrawerParams.drawerType) {
      case DrawerType.settingsOpen:
        return <Icon icon='cog' type='regular' size={24} />;
      default:
        return undefined;
    }
  }, [activeDrawerParams]);

  const drawerTitle = useMemo(() => {
    if (activeDrawerParams == null) {
      return '';
    }

    switch (activeDrawerParams.drawerType) {
      case DrawerType.settingsOpen:
        return 'Settings';
      default:
        return '';
    }
  }, [activeDrawerParams]);

  const drawerBody = useMemo(() => {
    if (!activeDrawerParams) {
      return undefined;
    }

    switch (activeDrawerParams.drawerType) {
      case DrawerType.settingsOpen:
        return <SettingsEditDrawer key={`document-create-${activeDrawerParams.drawerType}`} />;
      default:
        return undefined;
    }
  }, [activeDrawerParams]);

  const drawerTitleComponent = useMemo(
    () => <ItemTitle icon={drawerIcon} title={drawerTitle} />,
    [drawerIcon, drawerTitle],
  );

  return (
    <StyledDrawer
      closable
      title={overriddenDrawerTitle || drawerTitleComponent}
      width={drawerWidth}
      visible={activeDrawerParams != null}
      contentHeight={`calc(100% - ${theme.sizes.drawerTitleHeight}px)`}
      minWidth={defaultDrawerMinWidth}
      selectedItemsExist={selectedItemsExist}
      onClose={(closedByBlur: boolean) =>
        handleDrawerClose(closeDrawer, closedByBlur, formIsEdited)
      }
      onResize={isResizable ? updateDrawerWidth : undefined}
    >
      {drawerBody}
    </StyledDrawer>
  );
};

export default React.memo(DrawerPicker);
